// SAD-959: the items a refused commit names (the ApplyRefusal error detail),
// each with what is wrong, its values, what to do, and a link that opens it.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraInlineBanner, SpecraLink, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
/** This page's URL with ?set=&item= naming one item (the Center opens it). */ export function itemDeepLink(changeSetId, itemId) {
    const url = new URL(window.location.href);
    url.searchParams.set('set', changeSetId);
    url.searchParams.set('item', itemId);
    return `${url.pathname}${url.search}`;
}
export function CommitRefusalPanel({ refusals, changeSetId, onOpen }) {
    if (refusals.length === 0) return null;
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        title: refusals.length === 1 ? 'The commit was refused because of this item:' : `The commit was refused because of these ${refusals.length} items:`,
        "data-testid": Testids.modificationCenter.commitRefusals,
        "data-count": refusals.length,
        children: /*#__PURE__*/ _jsx("ul", {
            className: "mt-1 space-y-2",
            children: refusals.map((r, i)=>{
                const href = itemDeepLink(r.changeSetId || changeSetId, r.itemId);
                const target = [
                    r.targetLabel,
                    r.fieldLabel
                ].filter(Boolean).join(' · ');
                return /*#__PURE__*/ _jsxs("li", {
                    "data-testid": Testids.modificationCenter.commitRefusal,
                    "data-item-id": r.itemId,
                    "data-reason-code": r.reasonCode,
                    className: "space-y-0.5",
                    children: [
                        target && /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "body-ui",
                            className: "font-medium text-foreground",
                            children: target
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "body-ui",
                            children: r.reason
                        }),
                        (r.reviewedValue || r.currentValue) && /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            size: "meta",
                            tone: "muted",
                            children: [
                                r.reviewedValue && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "Reviewed: ",
                                        r.reviewedValue
                                    ]
                                }),
                                r.reviewedValue && r.currentValue && ' · ',
                                r.currentValue && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "On the contract now: ",
                                        r.currentValue
                                    ]
                                })
                            ]
                        }),
                        r.nextAction && /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "meta",
                            children: r.nextAction
                        }),
                        r.itemId && /*#__PURE__*/ _jsx(SpecraLink, {
                            href: href,
                            "data-testid": Testids.modificationCenter.commitRefusalOpenItem,
                            "data-item-id": r.itemId,
                            onClick: (e)=>{
                                e.preventDefault();
                                window.history.replaceState(window.history.state, '', href);
                                onOpen(r.itemId);
                            },
                            children: "Open item"
                        })
                    ]
                }, `${r.itemId}-${r.line}-${i}`);
            })
        })
    });
}
const FOCUS_RING = [
    'ring-2',
    'ring-primary',
    'ring-offset-1'
];
/**
 * Scrolls an item's row (a review item's, or a change item's in the line
 * table) into view and rings it for a moment; returns the cleanup.
 */ export function highlightItemRow(itemId) {
    let row = null;
    const at = (testid)=>Array.from(document.querySelectorAll(`[data-testid="${testid}"]`)).find((el)=>el.getAttribute('data-item-id') === itemId) ?? null;
    const raf = window.requestAnimationFrame(()=>{
        row = at(Testids.reviewItem.row) ?? at(Testids.modificationCenter.itemRow);
        if (!row) return;
        row.scrollIntoView?.({
            behavior: 'smooth',
            block: 'center'
        });
        row.classList.add(...FOCUS_RING);
        row.setAttribute('data-focused', 'true');
    });
    const unring = ()=>{
        row?.classList.remove(...FOCUS_RING);
        row?.removeAttribute('data-focused');
    };
    const timer = window.setTimeout(unring, 2500);
    return ()=>{
        window.cancelAnimationFrame(raf);
        window.clearTimeout(timer);
        unring();
    };
}
